/**
 * ============================================================================
 * x` GANTT EXECUTIVE TIMELINE - Pixel Perfect para Diretores Industriais
 * ============================================================================
 * 
 * Visualização Lean Office de nível executivo:
 * - Agrupamento por Recurso (apenas atividades)
 * - Cadeia de Valor Contínua (NVA preenchendo gaps)
 * - PCE Real baseado no Caminho Crítico
 * - Linha de Takt Time com alerta de sobrecarga
 * - Paleta de Cores Lean padronizada
 * - Fit-to-Width com escala inteligente
 * - Tooltips de desperdício com custo
 * 
 * @author Workflow Team
 * @version 2.0.0 - Executive Dashboard Edition
 * ============================================================================
 */

import React, { useMemo, useState } from 'react';
import { Clock, AlertTriangle, User, Zap, TrendingUp, DollarSign, Download } from 'lucide-react';
import { CardData } from '../../types';
import { transformToGantt, formatDuration, GanttTask } from '../../utils/ganttTransform';

// ============================================================================
// CONSTANTES - PALETA LEAN
// ============================================================================

const COLORS = {
  /** Trabalho (VA) - Azul Profundo */
  VA: '#1D4ED8',
  VA_GRADIENT: 'linear-gradient(90deg, #2563eb 0%, #1e40af 100%)',
  
  /** Espera (NVA) - Amarelo mbar com 30% opacidade */
  NVA: 'rgba(245, 158, 11, 0.22)',
  NVA_SOLID: '#d97706',
  NVA_PATTERN: 'repeating-linear-gradient(135deg, #f8fafc 0px, #f8fafc 5px, #eef2f7 5px, #eef2f7 10px)',
  
  /** Caminho Crítico - Vermelho */
  CRITICAL: '#dc2626',
  CRITICAL_BORDER: '#b91c1c',
  
  /** Takt Line - Vermelho pontilhado */
  TAKT_LINE: '#ef4444',
  
  /** Sobrecarga - Vermelho vibrante */
  OVERLOAD: '#dc2626',
  OVERLOAD_PULSE: '',
  
  /** Gap entre tarefas */
  GAP: 'rgba(148, 163, 184, 0.22)',
};

// ============================================================================
// INTERFACES
// ============================================================================

interface GanttChartProps {
  cards: CardData[];
  /** Takt Time em minutos (batimento do processo) */
  taktTime?: number;
  /** Custo por hora de espera para cálculo de desperdício */
  hourlyWaitCost?: number;
  /** Altura máxima do gráfico em pixels */
  maxHeight?: number;
}

interface TooltipData {
  visible: boolean;
  x: number;
  y: number;
  task: GanttTask | null;
  isNVA: boolean;
}

interface GanttRowData {
  key: string;
  label: string;
  subLabel?: string;
  tasks: GanttTask[];
  totalMinutes: number;
}

interface HoveredTaskData {
  rowKey: string;
  taskId: string;
  startMinutes: number;
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export const GanttChart: React.FC<GanttChartProps> = ({ 
  cards,
  taktTime = 12, // 12 min padrão
  hourlyWaitCost = 150, // R$ 150/hora custo médio
  maxHeight = 320
}) => {
  const [viewMode, setViewMode] = useState<'flow' | 'resource'>('flow');
  const chartAreaRef = React.useRef<HTMLDivElement | null>(null);
  // Estado do tooltip
  const [tooltip, setTooltip] = useState<TooltipData>({
    visible: false,
    x: 0,
    y: 0,
    task: null,
    isNVA: false
  });
  const [hoveredTask, setHoveredTask] = useState<HoveredTaskData | null>(null);

  // Transformar cards em estrutura Gantt
  // IMPORTANTE: Passar TODOS os cards para manter a cadeia de dependências
  // A filtragem de tipos é feita internamente na renderização
  const ganttData = useMemo(() => {
    return transformToGantt(cards);
  }, [cards]);
  
  const { tasks, totalDuration, criticalPath, stats, resourceGroups, zoom } = ganttData;

  // Calcular PCE Real (baseado no caminho crítico)
  const realPCE = useMemo(() => {
    if (criticalPath.length === 0 || totalDuration === 0) return 0;
    
    // Soma dos TRAs apenas do caminho crítico
    const criticalWorkTime = tasks
      .filter(t => criticalPath.includes(t.id) && !t.isGap)
      .reduce((sum, t) => sum + t.workDuration, 0);
    
    // PCE = (TRA do CP / Lead Time Total do CP) * 100
    return (criticalWorkTime / totalDuration) * 100;
  }, [tasks, criticalPath, totalDuration]);

  // Se não há tarefas, mostrar placeholder
  if (tasks.length === 0 || totalDuration === 0) {
    return (
      <div className="flex items-center justify-center h-full text-slate-400 dark:text-slate-500 text-sm">
        <Clock className="mr-2" size={16} />
        Adicione atividades ao mapa para visualizar o Gantt
      </div>
    );
  }

  // Ordenar recursos por ordem de entrada no fluxo (primeiro início no tempo).
  // Isso mantém a primeira atividade na primeira linha e evidencia paralelos.
  // Filtrar recursos ocultos (__hidden__) e 'Não Atribuído'.
  const sortedResources = useMemo(() => {
    const entries = Array.from(resourceGroups.entries())
      .filter(([resource]) => resource !== 'Não Atribuído' && resource !== '__hidden__');

    return entries.sort((a, b) => {
      const tasksA = a[1].filter(t => !t.isGap);
      const tasksB = b[1].filter(t => !t.isGap);

      const startA = tasksA.length > 0 ? Math.min(...tasksA.map(t => t.startMinutes)) : Number.POSITIVE_INFINITY;
      const startB = tasksB.length > 0 ? Math.min(...tasksB.map(t => t.startMinutes)) : Number.POSITIVE_INFINITY;
      const seqA = tasksA.length > 0 ? Math.min(...tasksA.map(t => t.sequence)) : Number.POSITIVE_INFINITY;
      const seqB = tasksB.length > 0 ? Math.min(...tasksB.map(t => t.sequence)) : Number.POSITIVE_INFINITY;
      const loadA = tasksA.reduce((sum, t) => sum + t.totalDuration, 0);
      const loadB = tasksB.reduce((sum, t) => sum + t.totalDuration, 0);

      if (viewMode === 'resource') {
        if (loadA !== loadB) return loadB - loadA;
        if (startA !== startB) return startA - startB;
        return a[0].localeCompare(b[0], 'pt-BR');
      }

      if (startA !== startB) return startA - startB;
      if (seqA !== seqB) return seqA - seqB;
      return a[0].localeCompare(b[0], 'pt-BR');
    });
  }, [resourceGroups, viewMode]);

  // Gerar marcas de tempo para o eixo X
  const timeMarks = useMemo(() => {
    const marks: number[] = [];
    const interval = zoom.tickInterval;
    for (let t = 0; t <= totalDuration; t += interval) {
      marks.push(t);
    }
    return marks;
  }, [totalDuration, zoom.tickInterval]);

  // Handlers de tooltip
  const showTooltip = (e: React.MouseEvent, task: GanttTask, isNVA: boolean) => {
    const chartRect = chartAreaRef.current?.getBoundingClientRect();
    const fallbackRect = e.currentTarget.getBoundingClientRect();
    const rect = chartRect ?? fallbackRect;
    const tooltipWidth = 340;
    setTooltip({
      visible: true,
      x: Math.min(Math.max(e.clientX - rect.left + 12, 8), Math.max(8, rect.width - tooltipWidth - 8)),
      y: Math.max(20, e.clientY - rect.top - 10),
      task,
      isNVA
    });
  };

  const hideTooltip = () => {
    setTooltip(prev => ({ ...prev, visible: false }));
  };

  // Calcular custo de espera
  const calculateWaitCost = (waitMinutes: number): string => {
    const hours = waitMinutes / 60;
    const cost = hours * hourlyWaitCost;
    return cost.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const formatMinutesShort = (value: number) => `${Math.max(0, Math.round(value))}m`;

  const flowRows = useMemo<GanttRowData[]>(() => {
    return tasks
      .filter((task) => !task.isGap && task.resource !== '__hidden__')
      .sort((a, b) => {
        if (a.sequence !== b.sequence) return a.sequence - b.sequence;
        if (a.startMinutes !== b.startMinutes) return a.startMinutes - b.startMinutes;
        return a.name.localeCompare(b.name, 'pt-BR');
      })
      .map((task) => ({
        key: task.id,
        label: task.name,
        subLabel: task.resource,
        tasks: [task],
        totalMinutes: task.workDuration + task.waitDuration,
      }));
  }, [tasks]);

  const resourceRows = useMemo<GanttRowData[]>(() => {
    return sortedResources.map(([resource, resourceTasks]) => ({
      key: `resource-${resource}`,
      label: resource,
      tasks: resourceTasks,
      totalMinutes: resourceTasks.reduce((sum, task) => {
        if (task.isGap) return sum;
        return sum + task.totalDuration;
      }, 0),
    }));
  }, [sortedResources]);

  const displayedRows = viewMode === 'flow' ? flowRows : resourceRows;

  return (
    <div className="h-full min-h-0 flex flex-col font-sans">
      {/* ================================================================== */}
      {/* HEADER - Estatísticas Executivas */}
      {/* ================================================================== */}
      <div className="shrink-0 flex items-center justify-between mb-4 px-3 py-2.5 bg-slate-100/80 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded">
        {/* Legenda */}
        <div className="flex items-center gap-5 text-[10px] font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">
          <span className="flex items-center gap-2">
            <div 
              className="w-4 h-3 rounded-sm shadow-sm" 
              style={{ background: COLORS.VA_GRADIENT }}
            />
            Trabalho (VA)
          </span>
          <span className="flex items-center gap-2">
            <div 
              className="w-4 h-3 rounded-sm border border-dashed border-slate-400"
              style={{ background: COLORS.NVA_PATTERN }}
            />
            Espera (NVA)
          </span>
          <span className="flex items-center gap-2">
            <div 
              className="w-3 h-3 bg-red-500/10 border border-red-500 rounded-[2px]"
              style={{ 
                boxShadow: `0 0 2px ${COLORS.TAKT_LINE}`
              }}
            />
            {'Refer\u00eancia de Ritmo (vis\u00edvel ao selecionar)'}
          </span>
          <span className="flex items-center gap-2">
            <div 
              className="w-4 h-3 rounded-sm" 
              style={{ 
                background: COLORS.VA,
                border: `2px solid ${COLORS.CRITICAL}`,
                boxShadow: `0 0 4px ${COLORS.CRITICAL}`
              }}
            />
            Caminho Crítico
          </span>
        </div>
        
        {/* KPIs */}
        <div className="flex items-center gap-4 text-[11px] text-slate-600 dark:text-slate-400">
          <div className="flex items-center gap-1 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 p-0.5">
            <button
              type="button"
              onClick={() => setViewMode('flow')}
              className={`px-2 py-0.5 text-[10px] font-semibold rounded ${
                viewMode === 'flow'
                  ? 'bg-slate-700 text-white'
                  : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
              }`}
            >
              Fluxo
            </button>
            <button
              type="button"
              onClick={() => setViewMode('resource')}
              className={`px-2 py-0.5 text-[10px] font-semibold rounded ${
                viewMode === 'resource'
                  ? 'bg-slate-700 text-white'
                  : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
              }`}
            >
              Recurso
            </button>
          </div>
          <button
            type="button"
            className="inline-flex items-center gap-1.5 px-2 py-1 text-[10px] font-semibold rounded border border-slate-300 dark:border-slate-600 bg-white/80 dark:bg-slate-800/80 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
          >
            <Download size={11} strokeWidth={1.5} className="text-slate-500 dark:text-slate-400" />
            Exportar PDF
          </button>
          <span className="flex items-center gap-1.5">
            <Clock size={13} className="text-slate-400" />
            Lead Time: 
            <b className="text-slate-800 dark:text-slate-200 ml-0.5">
              {formatDuration(totalDuration)}
            </b>
          </span>
          <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-sm bg-white dark:bg-slate-700 shadow-sm">
            <Zap size={13} className={realPCE >= 25 ? 'text-emerald-500' : 'text-amber-500'} />
            PCE: 
            <b className={`ml-0.5 ${realPCE >= 25 ? 'text-emerald-600' : 'text-amber-600'}`}>
              {realPCE.toFixed(1)}%
            </b>
          </span>
          <span className="flex items-center gap-1.5">
            <TrendingUp size={13} className="text-red-400" />
            Crítico: 
            <b className="text-red-600 ml-0.5">{criticalPath.length}</b>
          </span>
        </div>
      </div>

      {/* ================================================================== */}
      {/* GRÁFICO GANTT */}
      {/* ================================================================== */}
      <div ref={chartAreaRef} className="flex-1 min-h-0 relative overflow-x-hidden overflow-y-auto" style={{ minHeight: Math.min(maxHeight, 260) }}>
        <div className="w-full min-h-full">
          
          {/* Eixo X - Timeline */}
          <div className="flex items-end h-7 border-b border-slate-300 dark:border-slate-700 mb-2">
            <div className="w-36 shrink-0" />
            <div className="flex-1 relative">
              {timeMarks.map((time, idx) => (
                <div
                  key={idx}
                  className="absolute bottom-0 flex flex-col items-center"
                  style={{ left: `${(time / totalDuration) * 100}%`, transform: 'translateX(-50%)' }}
                >
                  <span className="text-[10px] text-slate-500 dark:text-slate-400 font-medium mb-1">
                    {formatDuration(time)}
                  </span>
                  <div className="w-px h-2 bg-slate-300 dark:bg-slate-600" />
                </div>
              ))}

            </div>
            <div className="w-14 shrink-0 bg-slate-50/50 border-l border-slate-200/70 text-right text-[10px] font-semibold text-slate-500 pr-1.5">TOTAL</div>
          </div>

          {displayedRows.map((row) => (
            <div key={row.key} className="flex items-center mb-1.5">
              <div className="w-36 shrink-0 pr-2 pl-1 text-left">
                <div
                  className="text-[11px] font-bold uppercase tracking-wide text-slate-500 truncate"
                  title={row.label}
                >
                  {row.label}
                </div>
                {row.subLabel && (
                  <div className="text-[9px] font-medium text-slate-400 truncate" title={row.subLabel}>
                    {row.subLabel}
                  </div>
                )}
              </div>

              <div className="flex-1 relative h-6 bg-white dark:bg-[var(--bg-surface)] border border-slate-200 dark:border-slate-700 rounded-sm overflow-visible">
                {timeMarks.map((time, idx) => (
                  <div
                    key={idx}
                    className="absolute top-0 bottom-0 border-l border-slate-100 dark:border-slate-800"
                    style={{ left: `${(time / totalDuration) * 100}%` }}
                  />
                ))}

                {row.tasks.map((task) => {
                  const isCritical = criticalPath.includes(task.id);
                  const waitDuration = Math.max(0, Number(task.waitDuration) || 0);
                  const rawWorkDuration = Math.max(0, Number(task.workDuration) || 0);
                  const rowBasedWorkFallback = Math.max(0, Number(row.totalMinutes) - waitDuration);
                  // Fallback visual: quando o TRA vier zerado mas o total da tarefa indicar carga restante,
                  // exibir esse delta como segmento azul para no "sumir" no grfico.
                  const workDuration =
                    rawWorkDuration > 0
                      ? rawWorkDuration
                      : Math.max(0, task.totalDuration - waitDuration, rowBasedWorkFallback);
                  const visualTaskMinutes = waitDuration + workDuration;
                  // Usa o maior valor para evitar perda visual do segmento TRA quando
                  // houver inconsistncia entre (wait + work) e totalDuration.
                  const timelineTaskMinutes = Math.max(task.totalDuration || 0, visualTaskMinutes);
                  const leftPercent = (task.startMinutes / totalDuration) * 100;
                  const totalWidthPercent = (timelineTaskMinutes / totalDuration) * 100;

                  if (task.isGap) {
                    return (
                      <div
                        key={task.id}
                        className="absolute top-1 bottom-1 rounded-sm cursor-pointer hover:brightness-95 transition-all"
                        style={{
                          left: `${leftPercent}%`,
                          width: `${totalWidthPercent}%`,
                          minWidth: '2px',
                          background: COLORS.GAP,
                          border: '1px dashed #cbd5e1',
                        }}
                        onMouseEnter={(e) => showTooltip(e, task, true)}
                        onMouseLeave={hideTooltip}
                      />
                    );
                  }

                  const waitWidthPercent =
                    timelineTaskMinutes > 0 ? Math.min(100, Math.max(0, (waitDuration / timelineTaskMinutes) * 100)) : 0;
                  const workWidthPercent =
                    timelineTaskMinutes > 0 ? Math.min(100, Math.max(0, (workDuration / timelineTaskMinutes) * 100)) : 0;
                  const workWidthCss = `${workWidthPercent}%`;
                  const waitWidthCss = `${waitWidthPercent}%`;
                  const efficiency =
                    timelineTaskMinutes > 0 ? (workDuration / timelineTaskMinutes) * 100 : 0;
                  const isHoveredTask = hoveredTask?.taskId === task.id && hoveredTask.rowKey === row.key;
                  const hoveredTaskTaktWidthPercent =
                    timelineTaskMinutes > 0 ? Math.min(100, (taktTime / timelineTaskMinutes) * 100) : 0;

                  return (
                    <div
                      key={task.id}
                      className="absolute relative z-[2] top-0 bottom-0 flex rounded-sm overflow-hidden"
                      style={{
                        left: `${leftPercent}%`,
                        width: `${totalWidthPercent}%`,
                        minWidth: '6px',
                        border: isCritical ? `1px solid ${COLORS.CRITICAL}` : '1px solid #cbd5e1',
                      }}
                      onMouseEnter={() => {
                        setHoveredTask({
                          rowKey: row.key,
                          taskId: task.id,
                          startMinutes: task.startMinutes,
                        });
                      }}
                      onMouseLeave={() => {
                        setHoveredTask((prev) => (prev?.taskId === task.id ? null : prev));
                        hideTooltip();
                      }}
                    >
                      {isHoveredTask && hoveredTaskTaktWidthPercent > 0 && (
                        <div
                          className="absolute inset-y-0 left-0 pointer-events-none z-[1] transition-all duration-150"
                          style={{
                            width: `${hoveredTaskTaktWidthPercent}%`,
                            background:
                              'linear-gradient(90deg, rgba(239, 68, 68, 0.14) 0%, rgba(239, 68, 68, 0.08) 100%)',
                            borderLeft: '2px solid #ef4444',
                          }}
                        />
                      )}

                      {waitDuration > 0 && (
                        <div
                          className="relative z-[2] h-full flex items-center justify-center hover:brightness-95 transition-all border-r border-dashed border-slate-300"
                          style={{
                            width: waitWidthCss,
                            minWidth: waitDuration > 0 ? '2px' : '0px',
                            background: COLORS.NVA_PATTERN,
                          }}
                          onMouseEnter={(e) => showTooltip(e, task, true)}
                          title={`Espera: ${formatMinutesShort(waitDuration)}`}
                        >
                          {waitWidthPercent > 15 && (
                            <span className="text-[9px] font-semibold text-slate-500 tabular-nums">
                              {formatMinutesShort(waitDuration)}
                            </span>
                          )}
                        </div>
                      )}

                      {workDuration > 0 && (
                        <div
                          className="relative z-[4] h-full flex items-center justify-center hover:brightness-95 transition-all shadow-sm flex-shrink-0 border-l border-white/20"
                          style={{
                            width: workWidthCss,
                            minWidth: workDuration > 0 ? '4px' : '0px',
                            flexShrink: 0,
                            background: '#2563EB',
                          }}
                          onMouseEnter={(e) => showTooltip(e, task, false)}
                          title={`Processamento: ${formatMinutesShort(workDuration)} | Efici\u00eancia: ${efficiency.toFixed(1)}%`}
                        >
                          {workWidthPercent > 15 && (
                            <span className="text-[9px] font-bold text-white tabular-nums">
                              {formatMinutesShort(workDuration)}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="w-14 shrink-0 pl-2 pr-1.5 text-right bg-slate-50/50 border-l border-slate-200/70">
                <span className="text-[11px] font-bold text-slate-700 tabular-nums">
                  {formatMinutesShort(row.totalMinutes)}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Tooltip Técnico */}
        {tooltip.visible && tooltip.task && (
          <div 
            className="absolute z-50 bg-slate-800 text-white text-[11px] rounded-md shadow-xl p-2.5 pointer-events-none border border-slate-700"
            style={{ 
              left: tooltip.x,
              top: tooltip.y,
              transform: 'translateY(-110%)',
              minWidth: '320px'
            }}
          >
            <div className="font-bold text-[11px] mb-1.5 pb-1 border-b border-slate-700">
              {tooltip.task.name}
            </div>
            <div className="text-slate-100 font-medium">
              {`Espera: ${formatMinutesShort(tooltip.task.waitDuration)} | Processamento: ${formatMinutesShort(
                tooltip.task.workDuration
              )} | Efici\u00eancia da Etapa: ${
                tooltip.task.totalDuration > 0
                  ? ((tooltip.task.workDuration / tooltip.task.totalDuration) * 100).toFixed(1)
                  : '0.0'
              }%`}
            </div>
          </div>
        )}
      </div>

      {/* ================================================================== */}
      {/* FOOTER - Resumo Executivo */}
      {/* ================================================================== */}
      <div className="shrink-0 mt-4 pt-3 pb-6 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between text-[11px] text-slate-600 dark:text-slate-400 px-3">
        <div className="flex items-center gap-5">
          <span className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS.VA }} />
            <b className="text-blue-600 dark:text-blue-400">{formatDuration(stats.totalWorkTime)}</b>
            <span className="text-slate-400">trabalho</span>
          </span>
          <span className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS.NVA_SOLID }} />
            <b className="text-amber-600 dark:text-amber-400">{formatDuration(stats.totalWaitTime)}</b>
            <span className="text-slate-400">espera</span>
          </span>
          <span className="flex items-center gap-1.5 text-red-500">
            <DollarSign size={12} />
            Custo NVA: <b>{calculateWaitCost(stats.totalWaitTime)}</b>
          </span>
          <span className="text-slate-500">
            PCE = <b className="text-slate-700 dark:text-slate-200">TRA / (TRA + ESP)</b>
          </span>
        </div>
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1">
            <User size={11} className="text-slate-400" />
            <b>{stats.resourceCount}</b> recursos
          </span>
          <span className="flex items-center gap-1 px-2 py-0.5 rounded bg-red-50 dark:bg-red-900/20">
            <AlertTriangle size={11} className="text-red-500" />
            <b className="text-red-600">{criticalPath.length}</b>
            <span className="text-red-500">críticas</span>
          </span>
        </div>
      </div>
    </div>
  );
};

